import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/** Expand eligible quantities; discount changes can also change Shopify's cart line keys. */
export function eligibleCartPens(cart, productId, previous = [], discountTitle = '') {
  const groups = new Map();
  for (const line of [...cart.items].reverse()) {
    if (String(line.product_id) !== String(productId) || line.selling_plan_allocation) continue;
    const properties = Object.entries(line.properties || {}).sort(([a], [b]) => a.localeCompare(b));
    const identity = JSON.stringify([String(line.variant_id), properties]);
    if (!groups.has(identity)) groups.set(identity, []);
    groups.get(identity).push(line);
  }
  const pens = [];
  for (const [identity, lines] of groups) {
    let ordinal = 0;
    // Identical units may share one averaged line, or have separate paid/free lines.
    lines.sort((a, b) => b.final_price - a.final_price);
    for (const line of lines) {
      const price = Number(line.original_price);
      const discount = (line.line_level_discount_allocations || []).reduce((sum, allocation) =>
        sum + (allocation.discount_application?.title === discountTitle ? Number(allocation.amount) : 0), 0);
      const freeCount = price > 0 && discount > 0 && discount % price === 0
        ? Math.min(line.quantity, discount / price) : 0;
      const paidCount = line.quantity - freeCount;
      const paidPrice = paidCount ? Math.floor(line.final_line_price / paidCount) : 0;
      const freeUnits = new Set(Array.from({ length: line.quantity }, (_, index) => index)
        .sort((a, b) => (b % 2 - a % 2) || a - b).slice(0, freeCount));
      let paidOrdinal = 0;
      for (let unit = 0; unit < line.quantity; unit++) {
        const free = freeUnits.has(unit);
        pens.push({ key: `${identity}:${ordinal++}`, variantId: String(line.variant_id),
          options: [...line.variant_options], originalPrice: price, free,
          price: free ? 0 : paidPrice + (++paidOrdinal === paidCount ? line.final_line_price % paidCount : 0),
        });
      }
    }
  }
  const order = new Map(previous.map((pen, index) => [pen.key, index]));
  return pens.sort((a, b) => (order.get(a.key) ?? Infinity) - (order.get(b.key) ?? Infinity));
}

/** Cart-confirmed numbered pens, retaining the native configurator and Ajax product form. */
class BogoSteps extends Component {
  #abort; #cartRequest; #observer; #formObserver; #timer;
  #active = 0; #page = 0; #openedNext = true;
  #drafts = []; #pens = []; #variants = [];
  #widget; #section; #summary; #defaults; #currency;
  #help = {}; #text = {}; #confirmed = false; #pending = false;

  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();
    this.#pending = false;
    this.#section = this.closest('.shopify-section');
    this.#widget = this.#section?.querySelector('[data-pen-product-variants]')?.parentElement;
    if (!this.#widget) return;
    this.#help = JSON.parse(this.querySelector('[data-nib-help]').textContent);
    this.#text = JSON.parse(this.querySelector('[data-bogo-text]').textContent);
    const signal = this.#abort.signal;
    this.addEventListener('click', this.#navigate, { signal });
    this.#section.addEventListener(ThemeEvents.variantUpdate, this.#refresh, { signal });
    this.#section.addEventListener('keydown', this.#keyboard, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#cartUpdated, { signal });
    document.addEventListener(ThemeEvents.discountUpdate, this.#cartUpdated, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#cartFailed, { signal });
    // Window capture precedes the theme's document-level native form handler.
    window.addEventListener('submit', this.#submitted, { capture: true, signal });
    window.addEventListener('pageshow', this.#resync, { signal });
    window.addEventListener('focus', this.#resync, { signal });
    Promise.all([customElements.whenDefined(this.#widget.localName), customElements.whenDefined('variant-picker')])
      .then(async () => {
        if (!this.isConnected || signal.aborted) return;
        const fullData = [...this.#widget.querySelectorAll('script[type="application/json"]')]
          .find(node => [...node.attributes].some(attr => attr.name.startsWith('data-pen-variants-full-')));
        const fullVariants = JSON.parse(fullData?.textContent || '[]');
        this.#variants = this.#widget.data.variants.map(variant => ({
          ...fullVariants.find(full => String(full.id) === String(variant.id)), ...variant,
        }));
        const current = this.#readDraft();
        const nibIndex = this.#widget.getOptionIndex('nib');
        const preferred = this.#variants.find(variant => variant.available &&
          variant.options[nibIndex]?.toLowerCase() === 'fine' &&
          variant.options.every((value, index) => index === nibIndex || value === current.options[index]));
        this.#defaults = { options: [...(preferred?.options || current.options)], capped: true };
        this.#drafts = [current, this.#newDraft()];
        if (!new URL(location.href).searchParams.has('variant') && preferred) this.#widget.restoreDraft(preferred.options, true);
        const formBlock = this.#section.querySelector('.buy-buttons-block');
        this.#summary = this.querySelector('[data-bogo-summary]');
        // Place only the summary by the native form, preserving the merchant's existing blocks.
        if (formBlock) formBlock.before(this.#summary);
        this.#summary.addEventListener('click', this.#navigate, { signal });
        for (const node of [this.#widget, this.#section.querySelector('.shopify-block[id*="ai_gen_block_a5c8cca"]'),
          this.#section.querySelector('product-price'), this.#section.querySelector('.bogo-included'), formBlock,
          this.#section.querySelector('sticky-add-to-cart')]) {
          if (node) node.dataset.bogoConfigurator = '';
        }
        this.#observer = new MutationObserver(this.#refresh);
        this.#observer.observe(this.#widget, {
          subtree: true, childList: true, attributes: true, attributeFilter: ['aria-pressed', 'class'],
        });
        const form = this.#section.querySelector('product-form-component');
        this.#formObserver = new MutationObserver(this.#updateButton);
        if (form) this.#formObserver.observe(form, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled'] });
        const sticky = this.#section.querySelector('sticky-add-to-cart');
        if (sticky) this.#formObserver.observe(sticky, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled'] });
        this.#section.addEventListener('change', this.#updateButton, { signal });
        this.#section.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#updateButton, { signal });
        this.#summary.hidden = false;
        this.#saveDraft();
        await this.#syncCart(true);
        this.#refresh();
      });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort(); this.#cartRequest?.abort();
    this.#observer?.disconnect(); this.#formObserver?.disconnect();
    clearTimeout(this.#timer);
    if (this.#summary) { this.#summary.hidden = true; this.append(this.#summary); }
    this.#section?.removeAttribute('data-bogo-review');
  }

  #newDraft() { return { options: [...this.#defaults.options], capped: this.#defaults.capped }; }
  #readDraft() { return { options: [...this.#widget.getSelectedOptions()], capped: this.#widget.isCapped }; }
  #penName(index) { return this.#text.pen.replace('[number]', String(index + 1)); }
  #money(price) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: this.#currency || this.dataset.currency,
    }).format(price / 100);
  }
  #slot(index) { return this.#pens[index] || this.#drafts[index]; }
  #saveDraft() {
    if (this.#defaults && this.#active >= this.#pens.length) this.#drafts[this.#active] = this.#readDraft();
  }
  #lastPage() {
    const count = this.#pens.length;
    return Math.max(0, Math.ceil(count / 2) - 1 + (this.#openedNext && count % 2 === 0 ? 1 : 0));
  }
  #select(index) {
    if (this.#pending) return;
    this.#saveDraft();
    if (index >= this.#pens.length) {
      const draft = this.#drafts[index] ||= this.#newDraft();
      if (!this.#widget.restoreDraft(draft.options, draft.capped)) return;
    }
    this.#active = index; this.#page = Math.floor(index / 2);
    this.#drafts[this.#page * 2 + 1] ||= this.#newDraft();
    this.#summary.querySelector('[data-bogo-status]').textContent = '';
    this.#refresh();
  }

  #navigate = event => {
    const button = event.target.closest('button');
    if (!button || button.disabled || this.#pending || !this.#defaults) return;
    if (button.hasAttribute('data-pen-step')) this.#select(this.#page * 2 + Number(button.dataset.penStep));
    if (button.hasAttribute('data-bogo-direction')) {
      const page = this.#page + Number(button.dataset.bogoDirection);
      if (page >= 0 && page <= this.#lastPage()) this.#select(page * 2);
    }
    if (button.hasAttribute('data-bogo-another') && this.#confirmed && this.#pens.length > 0 && this.#pens.length % 2 === 0) {
      this.#openedNext = true; this.#select(this.#pens.length);
    }
    if (button.hasAttribute('data-bogo-cart')) {
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer) drawer.open(); else location.assign(this.dataset.cartUrl);
    }
  };

  #submitted = event => {
    if (!this.#section?.contains(event.target) || !event.target.closest('product-form-component') || !this.#defaults) return;
    if (this.#pending || this.#active < this.#pens.length) {
      event.preventDefault(); event.stopImmediatePropagation(); return;
    }
    if (!event.target.checkValidity() || event.target.querySelector('button[name="add"]')?.disabled) return;
    const quantity = event.target.closest('product-form-component').querySelector('quantity-selector-component');
    if (quantity?.canAddToCart && !quantity.canAddToCart().canAdd) return;
    this.#saveDraft(); this.#pending = true; this.#render();
    clearTimeout(this.#timer);
    this.#timer = setTimeout(async () => {
      // Lost responses never trigger another add or optimistic advancement.
      this.#pending = false;
      await this.#syncCart(false);
      if (this.isConnected) this.#summary.querySelector('[data-bogo-status]').textContent = this.#text.unconfirmed;
    }, 12000);
  };
  #cartUpdated = event => {
    const ownAdd = event.detail?.data?.source === 'product-form-component' &&
      String(event.detail.data.productId) === this.dataset.productId && this.#pending;
    if (ownAdd) { this.#pending = false; clearTimeout(this.#timer); }
    this.#syncCart(false);
  };
  #cartFailed = event => {
    if (!this.#pending || !this.#section.contains(event.target)) return;
    this.#pending = false; clearTimeout(this.#timer); this.#syncCart(false);
  };
  #resync = () => { if (this.#defaults && !this.#pending) this.#syncCart(false); };

  async #syncCart(initial) {
    this.#cartRequest?.abort();
    const request = this.#cartRequest = new AbortController();
    this.#abort.signal.addEventListener('abort', () => request.abort(), { once: true, signal: request.signal });
    try {
      const response = await fetch(`${this.dataset.cartUrl}.js`, {
        headers: { Accept: 'application/json' }, signal: request.signal, cache: 'no-store',
      });
      if (!response.ok) throw new Error('Cart unavailable');
      const cart = await response.json();
      if (!Array.isArray(cart.items) || request.signal.aborted || !this.isConnected) return;
      const previousCount = this.#pens.length;
      const editing = this.#drafts[this.#active];
      this.#pens = eligibleCartPens(cart, this.dataset.productId, this.#pens, this.dataset.discountTitle);
      this.#currency = cart.currency; this.#confirmed = true;
      this.#summary.querySelector('[data-bogo-status]').textContent = '';
      const count = this.#pens.length;
      if (initial || count < previousCount) {
        this.#openedNext = true; this.#active = count; this.#page = Math.floor(count / 2);
        this.#drafts[count] = editing || this.#newDraft();
        this.#widget.restoreDraft(this.#drafts[count].options, this.#drafts[count].capped);
      } else if (count > previousCount) {
        this.#openedNext = count % 2 !== 0;
        // Completed pairs stay visible until the customer requests another pair.
        this.#active = count % 2 ? count : count - 1; this.#page = Math.floor(this.#active / 2);
        if (count % 2) {
          this.#drafts[count] ||= this.#newDraft();
          this.#widget.restoreDraft(this.#drafts[count].options, this.#drafts[count].capped);
        }
      }
      this.#page = Math.min(this.#page, this.#lastPage());
      this.#drafts[this.#page * 2 + 1] ||= this.#newDraft();
      this.#refresh();
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.#confirmed = false;
      this.#summary.querySelector('[data-bogo-status]').textContent = this.#text.unconfirmed;
      this.#render();
    }
  }

  #keyboard = event => {
    if (!['Enter', ' '].includes(event.key)) return;
    const button = event.target.closest('[data-ai-picker] button[data-value]');
    if (!button || button.disabled || !this.#widget.contains(button)) return;
    const option = button.closest('[data-ai-nib-buttons]') ? 'nib' : button.closest('[data-ai-color-col]') ? 'color' : 'clip';
    event.preventDefault(); this.#widget.selectOptionValue(this.#widget.getOptionIndex(option), button.dataset.value);
  };
  #refresh = () => {
    if (!this.#widget?.isConnected || !this.#defaults) return;
    this.#saveDraft();
    const draft = this.#readDraft();
    const nib = String(draft.options[this.#widget.getOptionIndex('nib')] || '').toLowerCase();
    const key = nib.includes('extra') || nib === 'ef' ? 'extra_fine' : nib.includes('1.1') ? 'italic_11' :
      nib.includes('1.5') ? 'italic_15' : nib.includes('1.9') ? 'italic_19' : nib === 'medium' || nib === 'm' ? 'medium' : 'fine';
    const note = this.#widget.querySelector('[data-bogo-nib-help]');
    const text = this.#help[key] || '';
    if (note && note.textContent !== text) {
      note.classList.remove('is-changing'); note.textContent = text; void note.offsetWidth; note.classList.add('is-changing');
    }
    this.#render();
  };
  #updateButton = () => {
    if (!this.#defaults) return;
    const quantity = Number(this.#section.querySelector('product-form-component input[name="quantity"]')?.value || 1);
    for (const label of this.#section.querySelectorAll('[data-add-to-cart-label], sticky-add-to-cart .add-to-cart-text__content > span:first-child')) {
      if (label.closest('button')?.disabled) continue;
      const text = quantity === 1 ? `${this.#text.add_to_cart} · ${this.#penName(this.#active)}` : this.#text.add_to_cart;
      if (label.textContent !== text) label.textContent = text;
    }
  };
  #render() {
    if (!this.#summary || !this.#defaults) return;
    const count = this.#pens.length;
    const review = this.#active < count;
    this.#section.dataset.bogoReview = String(review);
    this.#widget.querySelector('[data-ai-picker]').inert = this.#pending;
    this.#summary.querySelector('[data-bogo-count]').textContent = this.#confirmed ? `${count} ${this.#text.in_cart}` : '';
    this.#summary.querySelector('[data-bogo-cart-note]').hidden = count <= 2;
    for (const scope of [this, this.#summary]) {
      for (const button of scope.querySelectorAll('[data-bogo-direction]')) {
        const direction = Number(button.dataset.bogoDirection);
        button.disabled = this.#pending || (direction < 0 ? this.#page === 0 : this.#page === this.#lastPage());
      }
      for (const button of scope.querySelectorAll('[data-pen-step]')) {
        const index = this.#page * 2 + Number(button.dataset.penStep);
        const active = index === this.#active;
        button.disabled = this.#pending; button.setAttribute('aria-pressed', String(active));
        button.querySelector('[data-bogo-name]').textContent = this.#penName(index);
        const number = button.querySelector('.bogo-offer__number');
        if (number) number.textContent = index < count ? '✓' : String(index + 1);
        if (active) button.parentElement.setAttribute('aria-current', 'step');
        else button.parentElement.removeAttribute('aria-current');
      }
    }
    this.#summary.querySelectorAll('[data-bogo-card]').forEach((card, offset) => {
      const index = this.#page * 2 + offset;
      const slot = this.#slot(index);
      const variant = slot && this.#variants.find(variant => variant.options.every((value, i) => value === slot.options[i]));
      const inCart = index < count;
      card.querySelector('[data-bogo-card-status]').textContent = inCart ? (this.#confirmed ? this.#text.in_cart : '')
        : index === this.#active ? this.#text.editing : this.#text.choose;
      card.querySelector('[data-bogo-options]').textContent = slot?.options.join(' · ') || '';
      card.querySelector('[data-bogo-free]').hidden = !inCart || !this.#confirmed || !slot.free;
      card.querySelector('[data-bogo-price]').textContent = inCart && this.#confirmed ? this.#money(slot.price)
        : !inCart && variant ? this.#money(variant.price) : '';
      for (const layer of ['body', 'cap']) {
        const image = card.querySelector(`[data-bogo-image="${layer}"]`);
        const src = variant?.[layer]; image.hidden = !src;
        if (src && image.getAttribute('src') !== src) image.src = src;
      }
      card.querySelector('[data-bogo-preview]').hidden = !variant;
    });
    const another = this.#summary.querySelector('[data-bogo-another]');
    another.hidden = !this.#confirmed || count < 2 || count % 2 !== 0 || !review;
    another.disabled = this.#pending;
    this.#summary.querySelector('[data-bogo-cart]').hidden = !review;
    this.#updateButton();
  }
}

if (!customElements.get('bogo-steps')) customElements.define('bogo-steps', BogoSteps);
